import CheckCircleIcon from "@assets/images/check-circle.png";
import InfoIcon from "@assets/images/info.png";
import SearchIcon from "@assets/images/Search.png";
import CloseIcon from "@assets/images/x-circle.png";
import { Box, Button } from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import InputSearch from "@saleor/components/InputSearch";
import MyTypography from "@saleor/components/MyTypography";
import { SearchTypeEnum } from "@saleor/createOrderExport/views/SoldTo";
import {
  useExportSoldTosLazyQuery,
  useGetCustomerBlockLazyQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { mapEdgesToItems } from "@saleor/utils/maps";
import classNames from "classnames";
import uniqBy from "lodash/uniqBy";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import CustomerBlockDialog from "../CustomerBlockDialog";
import useStyles from "./styles";

interface TabProps {
  id: number;
  testId?: string;
  title: string;
}

enum MessageMode {
  WAITING = "WAITING",
  SOLD_TO_NOT_FOUND = "SOLD_TO_NOT_FOUND",
  SOLD_TO_EXISTS = "SOLD_TO_EXISTS",
  CREDIT_LIMIT = "CREDIT_LIMIT",
  CUSTOMER_BLOCK = "CUSTOMER_BLOCK"
}

interface ExportSearchBarProps {
  value?: string;
  onSearch: (value: string, type: SearchTypeEnum) => void;
  searchId: string;
  loading?: boolean;
  dataFound?: boolean;
  soldToExists?: boolean;
}

const SearchBar: FC<ExportSearchBarProps> = props => {
  const { onSearch, searchId, loading, dataFound, soldToExists } = props;
  const intl = useIntl();
  const [
    { searchIdExport, tabSoldToExport: tabActive },
    dispatchAppState
  ] = useAppState();
  const classes = useStyles();

  // const [error, setError] = useState<boolean>(false);
  const [piError, setPiError] = useState<boolean>(false);
  const [soldToError, setSoldToError] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");
  const [valueSoldToSelected, setValueSoldToSelected] = useState<string>("");
  const [mode, setMode] = useState<MessageMode | null>(MessageMode.WAITING);
  const [soldToMode, setSoldToMode] = useState<MessageMode | null>(
    MessageMode.WAITING
  );

  const error = useMemo(() => {
    if (tabActive === 1) {
      return soldToError;
    }
    return piError;
  }, [tabActive, piError, soldToError]);

  const setErrorByTab = (error: boolean) => {
    if (tabActive === 1) {
      setSoldToError(error);
    } else {
      setPiError(error);
    }
  };

  const [onOpenCustomerBlock, setOpenCustomerBlock] = useState<boolean>(false);

  const [getCustomerBlock] = useGetCustomerBlockLazyQuery({
    onCompleted: data => {
      if (!data.getCustomerBlock.customerBlock) {
        handleSearch();
      } else {
        setSoldToMode(MessageMode.CUSTOMER_BLOCK);
      }
    },
    fetchPolicy: "no-cache"
  });

  const [getCustomerBlockPI] = useGetCustomerBlockLazyQuery({
    onCompleted: data => {
      if (!data.getCustomerBlock.customerBlock) {
        handleSearch();
      } else {
        setModeByTab(null);
        setOpenCustomerBlock(true);
      }
    },
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!searchIdExport) {
      dispatchAppState({
        payload: {
          tabSoldToExport: 2
        },
        type: "setTabSoldToExport"
      });
    }
  }, []);

  const setTabActive = (tab: number) => {
    dispatchAppState({
      payload: {
        tabSoldToExport: tab
      },
      type: "setTabSoldToExport"
    });
  };

  const tabs: TabProps[] = [
    {
      id: 2,
      testId: "pi-number-tab",
      title: intl.formatMessage({
        defaultMessage: "เลขที่ PI",
        description: "label"
      })
    },
    {
      id: 1,
      testId: "sold-to-tab",
      title: intl.formatMessage({
        defaultMessage: "SOLD TO",
        description: "label"
      })
    }
  ];

  const handleSearch = () => {
    const checkError =
      tabActive === 1
        ? !valueSoldToSelected
          ? !value
          : !valueSoldToSelected
        : !value;
    const values = tabActive === 1 ? value || valueSoldToSelected : value;

    setErrorByTab(checkError);
    if (!error && values) {
      onSearch(
        values,
        tabActive === 1 ? SearchTypeEnum.SOLDTO : SearchTypeEnum.PI
      );
    }
  };

  const handleSelectedSearch = value => {
    setValueSoldToSelected(value);
    // if (!error && value) {
    //   onSearch(
    //     value,
    //     tabActive === 1 ? SearchTypeEnum.SOLDTO : SearchTypeEnum.PI
    //   );
    // }
  };
  const handleChange = event => {
    setValue(event);
    if (event) {
      setErrorByTab(false);
    } else {
      setModeByTab(MessageMode.WAITING);
    }
  };

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useExportSoldTosLazyQuery();

  const setModeByTab = mode => {
    if (tabActive === 1) {
      setSoldToMode(mode);
    } else {
      setMode(mode);
    }
  };

  useEffect(() => {
    if (soldToExists) {
      setModeByTab(MessageMode.SOLD_TO_EXISTS);
    } else if (!searchId) {
      setModeByTab(MessageMode.WAITING);
    } else if (searchId && !dataFound && !loading) {
      setModeByTab(MessageMode.SOLD_TO_NOT_FOUND);
    }
  }, [dataFound, searchId, soldToExists, loading]);

  useEffect(() => {
    if (value.length >= 2 && tabActive === 1) {
      getDataSuggestions({
        variables: {
          first: 100,
          filter: {
            search: value
          }
        }
      });
    }
  }, [value]);

  const dataOptions = useMemo(() => {
    const data = mapEdgesToItems(fetchDataSuggestions?.exportSoldTos) || [];
    const res = data.map(data => ({
      title: data.displayText,
      value: data.code
    }));
    return uniqBy([...res], "value");
  }, [fetchDataSuggestions?.exportSoldTos]);

  const inputPlaceHolder = useMemo(() => {
    if (tabActive === 2) {
      return intl.formatMessage({
        defaultMessage: "ค้นหาเลขที่ PI",
        description: "label"
      });
    } else {
      return intl.formatMessage({
        defaultMessage: "ค้นหา Sold to",
        description: "label"
      });
    }
  }, [tabActive]);
  const messageErrors = useMemo(() => {
    if (tabActive === 2) {
      return intl.formatMessage({
        defaultMessage: "กรุณากรอกข้อมูลที่ต้องการค้นหา",
        description: "label"
      });
    } else {
      return intl.formatMessage({
        defaultMessage: "กรุณากรอกรหัสลูกค้า",
        description: "label"
      });
    }
  }, [tabActive, error]);

  return (
    <>
      <Box className={classes.root}>
        <div className={classes.tabs}>
          {tabs.map((tab, index) => (
            <button
              id={tab.testId}
              data-testid={tab.testId}
              key={tab.id}
              onClick={() => {
                setTabActive(tab.id);
                // setErrorByTab(false);
                // setModeByTab(MessageMode.WAITING);
              }}
              className={classNames(classes.tabItem, {
                ["active"]: tab.id === tabActive
              })}
              style={{
                borderTopLeftRadius: index === 0 ? 6 : 0,
                borderTopRightRadius: index === tabs.length - 1 ? 6 : 0,
                border: 0
              }}
            >
              <MyTypography
                variant="subtitle2"
                style={{ color: tab.id === tabActive ? "#ffffff" : "#999999" }}
              >
                {tab.title}
              </MyTypography>
            </button>
          ))}
        </div>
        <div className={classes.searchBox}>
          <div className={classes.formControl}>
            <div style={{ display: tabActive === 1 ? "block" : "none" }}>
              <Autocomplete
                initialValue={searchIdExport}
                optionsProp={dataOptions}
                placeholderProps={inputPlaceHolder}
                onChangeProps={handleChange}
                loading={loadingDataSuggestion}
                onSearch={handleSelectedSearch}
                maxLengthProps={10}
                id="searchSoldTo"
                name="searchSoldTo"
                // isReset={tabActive !== 1}
              />
            </div>
            <div style={{ display: tabActive === 2 ? "block" : "none" }}>
              <InputSearch
                value={searchIdExport}
                variant="outlined"
                placeholder={inputPlaceHolder}
                inputType="number"
                maxLength={10}
                onChangeProps={handleChange}
                id="searchPI"
                name="searchPI"
                // isReset={tabActive !== 2}
              />
            </div>
            {/* {tabActive === 1 && (*/}
            {/*  <Autocomplete*/}
            {/*    initialValue={searchIdExport}*/}
            {/*    optionsProp={dataOptions}*/}
            {/*    placeholderProps={inputPlaceHolder}*/}
            {/*    onChangeProps={handleChange}*/}
            {/*    loading={loadingDataSuggestion}*/}
            {/*    onSearch={handleSelectedSearch}*/}
            {/*    isReset={tabActive !== 1}*/}
            {/*  />*/}
            {/* )}*/}
            {/* {tabActive === 2 && (*/}
            {/*  <InputSearch*/}
            {/*    value={searchIdExport}*/}
            {/*    variant="outlined"*/}
            {/*    placeholder={inputPlaceHolder}*/}
            {/*    inputType="number"*/}
            {/*    maxLength={30}*/}
            {/*    onChangeProps={handleChange}*/}
            {/*    isReset={tabActive !== 2}*/}
            {/*  />*/}
            {/* )}*/}
            <Button
              id="searchButton"
              name="searchButton"
              variant="contained"
              color="secondary"
              className={classes.button}
              disabled={loadingDataSuggestion || loading}
              onClick={() => {
                if (tabActive === 1) {
                  getCustomerBlock({
                    variables: {
                      input: {
                        soldToCode: valueSoldToSelected.split(" - ")[0] || value
                      }
                    }
                  });
                } else {
                  getCustomerBlockPI({
                    variables: {
                      input: {
                        contractNo: value
                      }
                    }
                  });
                }
              }}
            >
              <img src={SearchIcon} alt="search" />
            </Button>
          </div>
          {error ? (
            <MyTypography
              className={classes.errorMessage}
              variant="body3"
              style={{ marginTop: 2 }}
            >
              {messageErrors}
            </MyTypography>
          ) : null}
          {!error && (
            <div className={classes.info}>
              {/* {mode === MessageMode.WAITING && (
                <>
                  <img src={InfoIcon} alt="info" />
                  <MyTypography variant="body3">
                    {intl.formatMessage({
                      defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
                    })}
                  </MyTypography>
                </>
              )} */}
              <>
                <Msg mode={tabActive === 1 ? soldToMode : mode} />
              </>
            </div>
          )}
        </div>
      </Box>
      <CustomerBlockDialog
        open={onOpenCustomerBlock}
        onClose={() => {
          setOpenCustomerBlock(false);
        }}
      />
    </>
  );
};

const Msg = ({ mode }) => {
  const intl = useIntl();
  const classes = useStyles();
  switch (mode) {
    case MessageMode.SOLD_TO_EXISTS:
      // code block
      return (
        <>
          <img src={CheckCircleIcon} alt="success" />
          <MyTypography variant="body3" className={classes.successMessage}>
            {intl.formatMessage({
              defaultMessage: "พบรายการที่เกี่ยวข้อง"
            })}
          </MyTypography>
        </>
      );
    case MessageMode.SOLD_TO_NOT_FOUND:
      // code block
      return (
        <>
          <img src={CloseIcon} alt="close" />
          <MyTypography className={classes.errorMessage} variant="body3">
            {intl.formatMessage({
              defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
            })}
          </MyTypography>
        </>
      );
    case MessageMode.CUSTOMER_BLOCK:
      return (
        <>
          <img src={CloseIcon} alt="close" />
          <MyTypography className={classes.errorMessage} variant="body3">
            {intl.formatMessage({
              defaultMessage: "Customer Block"
            })}
          </MyTypography>
        </>
      );
    case MessageMode.WAITING:
      return (
        <>
          <img src={InfoIcon} alt="info" />
          <MyTypography variant="body3">
            {intl.formatMessage({
              defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
            })}
          </MyTypography>
        </>
      );
    default:
      return <></>;
    // code block
  }

  //  return  {mode === MessageMode.SOLD_TO_EXISTS && (
  //     <>
  //       <img src={CheckCircleIcon} alt="success" />
  //       <MyTypography
  //         variant="body3"
  //         className={classes.successMessage}
  //       >
  //         {intl.formatMessage({
  //           defaultMessage: "พบรายการที่เกี่ยวข้อง"
  //         })}
  //       </MyTypography>
  //     </>
  //   )}
  //   {mode === MessageMode.SOLD_TO_NOT_FOUND && (
  //     <>
  //       <img src={CloseIcon} alt="close" />
  //       <MyTypography
  //         className={classes.errorMessage}
  //         variant="body3"
  //       >
  //         {intl.formatMessage({
  //           defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
  //         })}
  //       </MyTypography>
  //     </>
  //   )}
  //   {mode === MessageMode.CUSTOMER_BLOCK && (
  //     <>
  //       <img src={CloseIcon} alt="close" />
  //       <MyTypography
  //         className={classes.errorMessage}
  //         variant="body3"
  //       >
  //         {intl.formatMessage({
  //           defaultMessage: "Customer Block"
  //         })}
  //       </MyTypography>
  //     </>
  //   )}
};

export default SearchBar;
